/**
 * Formattazione it-IT: date, valuta, numeri. Utility pure e
 * framework-agnostiche — se un domani servono anche a PDF/email lato server, si
 * promuovono a `@evolfin/shared`. I numeri vanno resi con
 * `font-variant-numeric: tabular-nums` a livello CSS.
 */

const fmtData = new Intl.DateTimeFormat('it-IT', { dateStyle: 'medium' });
const fmtDataOra = new Intl.DateTimeFormat('it-IT', { dateStyle: 'medium', timeStyle: 'short' });
// `useGrouping: 'always'`: i valori economici mostrano SEMPRE il separatore di
// migliaia (leggibilità), anche a 4 cifre — il default `min2` non lo farebbe.
const fmtEuro = new Intl.NumberFormat('it-IT', {
  style: 'currency',
  currency: 'EUR',
  useGrouping: 'always',
});
const fmtNumero = new Intl.NumberFormat('it-IT', { useGrouping: 'always' });

/** Normalizza l'input in `Date`; `null`/`undefined`/valori non validi → `null`. */
function aData(valore: Date | string | number | null | undefined): Date | null {
  if (valore == null) return null;
  const d = valore instanceof Date ? valore : new Date(valore);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Data senza orario, es. `4 ago 2026`. Stringa vuota se assente/non valida. */
export function formattaData(valore: Date | string | number | null | undefined): string {
  const d = aData(valore);
  return d ? fmtData.format(d) : '';
}

/** Data con orario, es. `4 ago 2026, 11:37`. */
export function formattaDataOra(valore: Date | string | number | null | undefined): string {
  const d = aData(valore);
  return d ? fmtDataOra.format(d) : '';
}

/** Importo in euro, es. `1.250,00 €`. */
export function formattaEuro(valore: number | null | undefined): string {
  return valore == null || Number.isNaN(valore) ? '' : fmtEuro.format(valore);
}

/**
 * Indirizzo su una riga sola, come lo si scriverebbe su una busta:
 * `Via Verdi 9, 37100 Verona (VR)`. Senza regione, che in elenco è rumore.
 */
export function formattaIndirizzo(indirizzo: {
  via: string;
  civico?: string;
  cap: string;
  comune: string;
  provincia: string;
}): string {
  const civico = indirizzo.civico ? ` ${indirizzo.civico}` : '';
  return `${indirizzo.via}${civico}, ${indirizzo.cap} ${indirizzo.comune} (${indirizzo.provincia})`;
}

/** Numero con separatori it-IT, es. `1.250`. */
export function formattaNumero(valore: number | null | undefined): string {
  return valore == null || Number.isNaN(valore) ? '' : fmtNumero.format(valore);
}

/**
 * Giorni residui fino a `scadenza` a partire da `da` (default: oggi), a
 * mezzanotte locale così "oggi" = 0 e ieri = -1 (imminenza scadenze).
 * `null` se la data non è valida.
 */
export function giorniResidui(
  scadenza: Date | string | number | null | undefined,
  da: Date = new Date(),
): number | null {
  const fine = aData(scadenza);
  if (!fine) return null;
  const inizioGiorno = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const MS_GIORNO = 1000 * 60 * 60 * 24;
  return Math.round((inizioGiorno(fine) - inizioGiorno(da)) / MS_GIORNO);
}

/** Esito di `giorniResidui` in prosa, es. `tra 3 giorni` / `scaduta da 2 giorni`. */
export function formattaGiorniResidui(giorni: number): string {
  // Il giorno corrente ha una forma sua: «tra 0 giorni» non si dice.
  if (giorni === 0) return 'oggi';
  const assoluto = Math.abs(giorni);
  const unita = assoluto === 1 ? 'giorno' : 'giorni';
  return giorni > 0 ? `tra ${giorni} ${unita}` : `scaduta da ${assoluto} ${unita}`;
}
